import React, { useContext, useEffect, useRef, useState } from "react";
import UserServices from "../../../UserServices/UserServices";
import { NoteState } from "../../../context/notes/NoteState";
import { Link, useLocation, useNavigate } from "react-router-dom";
import MenuButton from "../../MenuButton/MenuButton";
import MealPlanIntakeForm from "./MealPlanIntakeForm";
import { toast } from "react-toastify";
import { createEmptyWeeks, buildPlanPayload } from "./MealPlanCell";
import {
  emptyMealPlanIntakeForm,
  normalizeMealPlanIntakeForm,
  sanitizeMealPlanIntakeFormForSave,
  mergeSavedIntakeForm,
  collectIntakeFormImageUploads,
} from "./mealPlanIntakeFormData";
import {
  fetchClientIntakeDefaults,
  resolveClientIdFromInput,
} from "./mealPlanClientAutofill";

export default function AddMealPlan() {
  const { state } = useContext(NoteState);
  const { branch_id } = state;
  const location = useLocation();
  const navigate = useNavigate();
  const autofillRequestRef = useRef(0);

  const [loading, setLoading] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [myBranches, setMyBranches] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientInput, setClientInput] = useState("");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedBranch, setselectedBranch] = useState(branch_id ? branch_id : "");
  const [selectedStartTime, setselectedStartTime] = useState("");
  const [selectedEndTime, setselectedEndTime] = useState("");
  const [intakeForm, setIntakeForm] = useState(emptyMealPlanIntakeForm());

  const branches = async () => {
    try {
      const responce = await UserServices.branchName();
      const res = responce.data.data;
      setMyBranches([
        { name: "Select Branches", id: "" },
        ...res.map((value) => ({ id: value.id, name: value.name })),
      ]);
    } catch (err) {
      console.log(err);
    }
  };

  const nameClients = async () => {
    try {
      const responce = await UserServices.getClientsName(branch_id);
      setClients(
        responce.data.data.map((value) => ({
          id: value.id,
          name: `${value.first_name} ${value.last_name}`.trim(),
        }))
      );
    } catch (err) {
      console.log(err);
      setClients([]);
    }
  };

  const applyClientAutofill = async (clientId, clientName = "") => {
    if (!clientId) return;
    const requestId = ++autofillRequestRef.current;
    setPrefilling(true);
    try {
      const defaults = await fetchClientIntakeDefaults(clientId, {
        name: clientName || undefined,
      });
      if (requestId !== autofillRequestRef.current) return;
      setIntakeForm(defaults);
    } catch (err) {
      console.log(err);
    }
    setPrefilling(false);
  };

  const handleClientInputChange = (value) => {
    setClientInput(value);
    const clientId = resolveClientIdFromInput(value, clients);
    if (clientId && clientId !== selectedClientId) {
      setSelectedClientId(clientId);
      applyClientAutofill(clientId, value);
    } else if (!clientId) {
      setSelectedClientId("");
    }
  };

  useEffect(() => {
    branches();
    nameClients();
  }, []);

  useEffect(() => {
    if (!location.state?.clientId || !clients.length) return;
    const matched = clients.find(
      (client) => String(client.id) === String(location.state.clientId)
    );
    const clientName = location.state.clientName || matched?.name || "";
    setClientInput(clientName);
    setSelectedClientId(String(location.state.clientId));
    applyClientAutofill(location.state.clientId, clientName);
  }, [location.state, clients]);

  const addMeal = async () => {
    if (!selectedClientId) {
      toast.error("Please select client!", { position: "top-center" });
      return;
    }
    if (!selectedStartTime) {
      toast.error("Please select start date!", { position: "top-center" });
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("branch_id", selectedBranch);
      formData.append("client_id", selectedClientId);
      formData.append("start_date", selectedStartTime);
      formData.append("end_date", selectedEndTime);
      formData.append("plan", JSON.stringify(buildPlanPayload(createEmptyWeeks())));

      const res = await UserServices.addMealPlanByMonth(formData);
      const planUuid = res?.data?.data?.uuid;

      if (planUuid) {
        const uploads = collectIntakeFormImageUploads(intakeForm);
        const saveRes = await UserServices.saveMealPlanIntakeForm(
          planUuid,
          sanitizeMealPlanIntakeFormForSave(intakeForm),
          uploads
        );
        if (saveRes?.data?.status) {
          setIntakeForm(mergeSavedIntakeForm(saveRes.data.data, intakeForm));
        }
      }

      toast.success("Diet plan added successfully!", { position: "top-center" });
      if (planUuid) {
        navigate(`/detail-meal-plan/${planUuid}`);
      }
    } catch (err) {
      console.log(err);
      toast.error("Could not save diet plan", { position: "top-center" });
    }
    setLoading(false);
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />

          <div className="card">
            <div className="card-header py-3">
              <h5 className="mb-0 font-20">
                Add Diet Plan
                <Link
                  to="/view-meal-plan"
                  className="btn btn-primary d-inline-block float-end btn-sm mt-3 mt-md-0"
                >
                  View Diet Plans
                </Link>
              </h5>
            </div>
            <div className="card-body">
              <div className="row g-3 mb-3">
                <div className="col-lg-3">
                  <label className="form-label">
                    Branch Name<span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    disabled={!!branch_id}
                    value={selectedBranch}
                    onChange={(e) => setselectedBranch(e.target.value)}
                  >
                    {myBranches.map((el, index) => (
                      <option key={index} value={el.id}>
                        {el.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-3">
                  <label className="form-label">
                    Client Name<span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    list="member_id"
                    value={clientInput}
                    onChange={(e) => handleClientInputChange(e.target.value)}
                    onBlur={(e) => {
                      const clientId = resolveClientIdFromInput(e.target.value, clients);
                      if (clientId) applyClientAutofill(clientId, e.target.value);
                    }}
                  />
                  <datalist id="member_id">
                    {clients.map((el, index) => (
                      <option key={index} value={el.name} />
                    ))}
                  </datalist>
                  {prefilling ? (
                    <small className="text-muted">Loading client details…</small>
                  ) : null}
                </div>
                <div className="col-lg-3">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    onChange={(e) => {
                      setselectedStartTime(e.target.value);
                      const startDate = new Date(e.target.value);
                      const endDate = new Date(startDate.getTime() + 30 * 24 * 60 * 60 * 1000);
                      setselectedEndTime(endDate.toISOString().slice(0, 10));
                    }}
                  />
                </div>
                <div className="col-lg-3">
                  <label className="form-label">End Date</label>
                  <input type="date" className="form-control" value={selectedEndTime} disabled />
                </div>
              </div>

              <MealPlanIntakeForm form={intakeForm} onChange={setIntakeForm} branchId={selectedBranch} />

              <button className="btn btn-primary w-100 mt-3" onClick={addMeal}>
                Save Diet Plan
              </button>
            </div>
          </div>
        </div>
      </main>
      <div className={`loader ${loading || prefilling ? "in" : ""}`}>
        <div className="spinner-border main-spin"></div>
      </div>
    </div>
  );
}

